import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Table, Button, Modal, ModalHeader, 
  ModalBody, Form, FormGroup, Label, Input, Spinner, Badge
} from "reactstrap";
import { get, post_new, del } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const CustomFields = () => {
  document.title = "Câmpuri Personalizate | PagaSunt Admin";

  const [fields, setFields] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // Initial State for a field
  const initialState = {
    id: null,
    name: "",
    type: "text",
    placeholder: "",
    is_required: false,
    parent_field_id: "", // NEW: Reference to parent field
    categories: [], 
    options: [{ id: null, value: "", parent_option_id: "" }] // NEW: added parent_option_id
  };

  const [currentField, setCurrentField] = useState(initialState);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const fieldRes = await get("/admin/custom-fields");
      const catRes = await get("/admin/categories");
      
      setFields(fieldRes);
      setCategories(catRes.status === "success" ? catRes.data.flat : []);
    } catch (error) {
      console.error("Error fetching data", error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchFields(); }, []);

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setIsEdit(false);
      setCurrentField(initialState);
    }
  };

  // --- Options Logic ---
  const handleAddOption = () => {
    setCurrentField({ 
        ...currentField, 
        options: [...currentField.options, { id: null, value: "", parent_option_id: "" }] 
    });
  };

  const handleRemoveOption = (index) => {
    const newOptions = currentField.options.filter((_, i) => i !== index);
    setCurrentField({ ...currentField, options: newOptions });
  };

  const handleOptionChange = (index, key, val) => {
    const newOptions = [...currentField.options];
    newOptions[index][key] = val;
    setCurrentField({ ...currentField, options: newOptions });
  };

  // --- Category Selection Logic ---
  const handleCategoryToggle = (catId) => {
    const updatedCats = currentField.categories.includes(catId)
      ? currentField.categories.filter(id => id !== catId)
      : [...currentField.categories, catId];
    setCurrentField({ ...currentField, categories: updatedCats });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isChoiceType = ["select", "radio", "checkbox"].includes(currentField.type);
    
    const payload = {
      name: currentField.name,
      type: currentField.type,
      placeholder: currentField.placeholder,
      is_required: currentField.is_required ? 1 : 0,
      parent_field_id: currentField.parent_field_id || null, // NEW
      categories: currentField.categories,
      options: isChoiceType 
        ? currentField.options
            .filter(opt => opt.value.trim() !== "")
            .map(opt => ({
                id: opt.id,
                value: opt.value,
                parent_option_id: opt.parent_option_id || null // NEW
            })) 
        : []
    };

    try {
      if (isEdit) {
        await post_new(`/admin/custom-fields/${currentField.id}`, payload);
      } else {
        await post_new("/admin/custom-fields", payload);
      }
      fetchFields();
      toggleModal();
    } catch (error) {
      console.error("Error saving field", error);
      alert("Eroare la salvarea câmpului.");
    }
  };

  const handleEdit = (field) => {
    setCurrentField({
      id: field.id,
      name: field.name,
      type: field.type,
      placeholder: field.placeholder || "",
      is_required: field.is_required === 1 || field.is_required === true,
      parent_field_id: field.parent_field_id || "",
      categories: field.categories.map(c => c.id),
      options: field.options.length > 0 
        ? field.options.map(o => ({ id: o.id, value: o.value, parent_option_id: o.parent_option_id || "" })) 
        : [{ id: null, value: "", parent_option_id: "" }]
    });
    setIsEdit(true);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Sigur vrei să ștergi acest câmp? Toate valorile anunțurilor asociate vor fi șterse!")) {
      await del(`/admin/custom-fields/${id}`);
      fetchFields();
    }
  };

  // Helper to find the options of the selected parent field
  const getParentOptions = () => {
    if (!currentField.parent_field_id) return [];
    const parentField = fields.find(f => f.id === parseInt(currentField.parent_field_id));
    return parentField ? parentField.options : [];
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PagaSunt" breadcrumbItem="Câmpuri Personalizate" />

          <Row className="mb-3">
            <Col>
              <Button color="primary" onClick={toggleModal}>
                <i className="mdi mdi-plus me-1"></i> Adaugă Câmp Nou
              </Button>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? <div className="text-center"><Spinner /></div> : (
                    <Table responsive className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Nume Câmp</th>
                          <th>Tip</th>
                          <th>Dependență</th>
                          <th>Categorii Asociate</th>
                          <th>Opțiuni</th>
                          <th>Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field) => (
                          <tr key={field.id}>
                            <td>
                              <strong>{field.name}</strong>
                              {field.is_required ? <Badge color="danger" className="ms-2">Obligatoriu</Badge> : null}
                            </td>
                            <td><Badge color="info" outline>{field.type.toUpperCase()}</Badge></td>
                            <td>
                              {field.parent_field ? (
                                <span>Depinde de: <strong>{field.parent_field.name}</strong></span>
                              ) : (
                                <span className="text-muted">Niciuna</span>
                              )}
                            </td>
                            <td>
                              {field.categories?.map(cat => (
                                <Badge key={cat.id} color="secondary" className="me-1">{cat.name}</Badge>
                              ))}
                            </td>
                            <td>{field.options_count || field.options?.length || 0} opțiuni</td>
                            <td>
                              <Button size="sm" color="link" onClick={() => handleEdit(field)}>Edit</Button>
                              <Button size="sm" color="link" className="text-danger" onClick={() => handleDelete(field.id)}>Șterge</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>{isEdit ? "Editează Câmpul" : "Câmp Nou"}</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Nume Câmp (ex: Kilometraj)</Label>
                  <Input 
                    type="text" 
                    value={currentField.name} 
                    onChange={(e) => setCurrentField({ ...currentField, name: e.target.value })} 
                    required 
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Tip Câmp</Label>
                  <Input 
                    type="select" 
                    value={currentField.type} 
                    onChange={(e) => setCurrentField({ ...currentField, type: e.target.value })}
                  >
                    <option value="text">Text scurt</option>
                    <option value="number">Număr</option>
                    <option value="textarea">Text lung (Textarea)</option>
                    <option value="select">Dropdown (Select)</option>
                    <option value="radio">Buton Radio</option>
                    <option value="checkbox">Bifă (Checkbox)</option>
                    <option value="date">Dată</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Câmp Părinte (Opțional)</Label>
                  <Input 
                    type="select" 
                    value={currentField.parent_field_id} 
                    onChange={(e) => setCurrentField({ ...currentField, parent_field_id: e.target.value })}
                  >
                    <option value="">Fără dependență</option>
                    {fields
                        .filter(f => f.id !== currentField.id && ["select", "radio"].includes(f.type))
                        .map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                        ))
                    }
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Placeholder</Label>
              <Input 
                type="text" 
                value={currentField.placeholder} 
                onChange={(e) => setCurrentField({ ...currentField, placeholder: e.target.value })} 
              />
            </FormGroup>

            <FormGroup check className="mb-3">
              <Label check>
                <Input 
                  type="checkbox" 
                  checked={currentField.is_required} 
                  onChange={(e) => setCurrentField({ ...currentField, is_required: e.target.checked })} 
                />{' '}
                Acest câmp este obligatoriu la postare
              </Label>
            </FormGroup>

            <hr />

            <Label><strong>Categorii disponibile:</strong></Label>
            <div 
              style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ced4da", padding: "10px", borderRadius: "6px" }} 
              className="mb-3 bg-light"
            >
              {categories.map(cat => (
                <div key={cat.id} className="form-check" style={{ marginLeft: `${(cat.depth || 0) * 20}px` }}>
                  <Input 
                    type="checkbox" 
                    id={`cat-${cat.id}`}
                    checked={currentField.categories.includes(cat.id)}
                    onChange={() => handleCategoryToggle(cat.id)}
                  />
                  <Label className="form-check-label ms-2" htmlFor={`cat-${cat.id}`}>
                    {cat.depth > 0 ? "└─ " : ""} {cat.name}
                  </Label>
                </div>
              ))}
            </div>

            {/* Dynamic Options Section */}
            {["select", "radio", "checkbox"].includes(currentField.type) && (
              <div className="mt-3 p-3 border rounded bg-light">
                <Label><strong>Gestionare Opțiuni</strong></Label>
                {currentField.options.map((option, index) => (
                  <Row key={index} className="mb-2 gx-2">
                    <Col md={currentField.parent_field_id ? 6 : 10}>
                      <Input 
                        type="text" 
                        placeholder="Valoare opțiune (ex: BMW sau 3 Series)" 
                        value={option.value} 
                        onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                        required
                      />
                    </Col>
                    
                    {/* NEW: Parent Option Selector */}
                    {currentField.parent_field_id && (
                      <Col md={4}>
                        <Input 
                          type="select"
                          value={option.parent_option_id}
                          onChange={(e) => handleOptionChange(index, 'parent_option_id', e.target.value)}
                          required
                        >
                          <option value="">-- Alege Părinte --</option>
                          {getParentOptions().map(po => (
                            <option key={po.id} value={po.id}>{po.value}</option>
                          ))}
                        </Input>
                      </Col>
                    )}

                    <Col md={2} className="text-end">
                      <Button 
                        color="danger" 
                        outline 
                        onClick={() => handleRemoveOption(index)}
                        disabled={currentField.options.length === 1}
                      >
                        <i className="mdi mdi-delete"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button color="success" size="sm" outline onClick={handleAddOption} className="mt-2">
                  <i className="mdi mdi-plus me-1"></i> Adaugă Opțiune
                </Button>
              </div>
            )}

            <Button color="primary" type="submit" className="mt-4 w-100">
              {isEdit ? "Actualizează Câmpul" : "Salvează Câmpul"}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CustomFields;